<template>
    <div class="jobs-root">
        <Header :label="$t('findjobs.header')" overlap="small" ref="top" />

        <BaseSidebar v-if="showSidebar" v-model="showSidebar">
            <SearchModule
                filterOrientation="vertical"
                hideSearch
                :filters="getFilters()"
                v-model="selected"
                v-on:clear:filter="
                    () => {
                        clearFilters();
                        resetPageScroll();
                    }
                "
                class="flex-grow"
                v-on:filter:change="resetPageScroll"
                :hasFiltersChanged="hasFiltersChanged"
            />
        </BaseSidebar>

        <MainLayout hideOnMobile="left right">
            <ProfileModule slot="left" />
            <FeaturedResource slot="left" />

            <!-- <RecruitmentModule
                v-if="!isLoggedIn"
                slot="bottomLeftToBottomRight"
            /> -->

            <SearchModule
                filterOrientation="horizontal"
                slot="top"
                :filters="getFilters()"
                v-model="selected"
                v-on:clear:filter="
                    () => {
                        clearFilters();
                        resetPageScroll();
                    }
                "
                v-on:filter:change="resetPageScroll"
                :hasFiltersChanged="hasFiltersChanged"
            />

            <div slot="mobtop">
                <SearchModule
                    filterOrientation="horizontal"
                    v-model="selected"
                    v-on:clear:filter="
                        () => {
                            clearFilters();
                            resetPageScroll();
                        }
                    "
                    v-on:filter:change="resetPageScroll"
                    :hasFiltersChanged="hasFiltersChanged"
                >
                    <div
                        class="text-center py-2 subheading secondary-color noselect"
                        @click="() => (showSidebar = !showSidebar)"
                    >
                        {{ $t('findjobs.changeFilters') }}
                    </div>
                </SearchModule>
            </div>

            <Pagination slot="center" v-on:onBottom="nextPage" ref="pagination">
                <PostingModule
                    :data="findJobsData"
                    :pageOffset="test[0]"
                    :label="$t('shared.postingModule.postingModule.positions')"
                    :isLoading="loading()"
                    :fakeLoad="shouldFakeLoad"
                    :estimatedJobsLeft="estimatedJobsLeft"
                />
            </Pagination>
            <FeaturedPositions slot="right" />
            <FeaturedCompanies slot="right" />
        </MainLayout>
    </div>
</template>

<script>
// Project components
import Header from '../../components/layouts/Header';
import MainLayout from '../../components/layouts/mainlayout/MainLayout';
import SearchModule from '../../shared/searchmodule/SearchModule';
import ProfileModule from '~/components/smart/ProfileModule/ProfileModule';
import PostingModule from '~/shared/postingmodule/PostingModule';
import FeaturedPositions from '~/components/smart/FeaturedPositions';
import FeaturedCompanies from '~/components/smart/FeaturedCompanies';
import Pagination from '~/components/miscellaneous/Pagination';
import RecruitmentModule from './components/RecruitmentModule';
import FeaturedResource from '~/components/smart/FeaturedResource';

// Utils
import mapSelectedToFilters from '~/utils/mappers/mapSelectedToFilters';
import emptySelected from '~/utils/emptySelected';

// Filter data
import jobFilters, { i18nMap, filterMap } from '~/data/jobFilters';

import { getSubDomain } from '~/utils/urlUtils';

import AuthService from '~/services/AuthService';

export default {
    name: 'FindJobs',
    components: {
        Header,
        MainLayout,
        SearchModule,
        PostingModule,
        ProfileModule,
        FeaturedPositions,
        FeaturedCompanies,
        FeaturedResource,
        Pagination,
        RecruitmentModule,
    },
    data() {
        return {
            selected: null,
            scrollState: 0, // The stored scroll value
            showSidebar: false,
            waitForFilterProcessing: true,
            forceFilterUpdate: false,
        };
    },
    computed: {
        selectedFilters() {
            if (
                Object.values(this.selected).reduce(
                    (prev, curr) => prev + Object.values(curr).length,
                    0
                ) === 0
            )
                return {};
            else return this.selected;
        },
        isLoggedIn() {
            return AuthService.isLoggedIn();
        },
        queries() {
            return this.$route.query;
        },
        findJobsData() {
            let [counter, jobsWithAds] = this.test;
            return [...jobsWithAds, ...this.filteredData.slice(counter)];
        },
        test() {
            // // console.log(typeof this.featuredData, typeof this.data());
            // console.log("Filtered data", this.filteredData.map(it => it.jobAdId))
            // console.log("Featured data", this.featuredData.map(it => it.jobAdId))
            // console.log("Relevant data", this.relevantFeaturedData.map(it => it.jobAdId))
            // console.log("Unrelevant data", this.unrelevantFeaturedData.map(it => it.jobAdId))
            let counter = 0;
            let temp = [];
            for (let i = 0; i < this.relevantFeaturedData.length; i += 3) {
                temp.push(
                    ...this.relevantFeaturedData.slice(i, i + 3),
                    ...this.filteredData.slice(i, i + 3)
                );
                counter = i + 3;
            }
            for (let i = 0; i < this.unrelevantFeaturedData.length; i++) {
                // console.log("Unrelevant data", i, this.unrelevantFeaturedData[i].id)
                temp.push(
                    this.unrelevantFeaturedData[i],
                    ...this.filteredData.slice(i + counter, i + counter + 5)
                );
                counter = i + 5;
            }

            // console.log('TEMP', temp);
            return [counter, temp];
        },
        filteredData() {
            return this.data.filter(
                it => !this.featuredData.find(it2 => it.id == it2.id)
            );
        },
        featured() {
            const featured = this.$interfaces.posting.get({
                reactive: true,
                ...mapSelectedToFilters(this.selectedFilters),
                mode: 'featured',
                page_size: 9,
            });
            return featured;
        },
        hasFilters() {
            return Object.values(this.selectedFilters).find(
                it => Object.keys(it).length > 0
            );
        },
        relalancySplit() {
            let threshold = this.hasFilter ? 25 : 50;
            return this.featuredData.findIndex(
                it =>
                    !(this.postings.data || [])
                        .slice(0, threshold)
                        .find(it2 => it.id == it2.id)
            );
        },
        relevantFeaturedData() {
            return this.featuredData.slice(0, this.relalancySplit);
        },
        unrelevantFeaturedData() {
            return this.featuredData.slice(this.relalancySplit).map(it => ({
                ...it,
                ['promote']: true,
            }));
        },
        featuredData() {
            // if (this.featured.loading || this.postings.loading || this.selected.query) return [];
            if (this.featured.loading) return [];
            // console.log(this.featured.data);
            return IS_PROD
                ? this.featured.data
                : this.featured.data.map(it => ({
                      ...it,
                      //   ['promote']: true,
                      ['labels']: [
                          ...it['labels'],
                          {
                              text: 'Ad',
                              tooltip: 'This posting is featured',
                          },
                      ],
                  }));
        },
        postings() {
            if (this.waitForFilterProcessing) return { loading: true };
            // console.log(mapSelectedToFilters(this.selected));
            const data = this.$interfaces.posting.get(
                {
                    reactive: true,
                    ...mapSelectedToFilters(this.selectedFilters),
                },
                1,
                this.hasFilters ? this.page : this.page + 1
            );
            return data;
        },
        estimatedJobsLeft() {
            if (this.postings instanceof Array || this.postings.loading) {
                return;
            } else {
                return this.postings.queryInfo.estimatedJobsLeft;
            }
        },
        page() {
            return this.$interfaces.scrollState.findJobsPage();
        },
        connections() {
            return this.$interfaces.university.connection.get({
                reactive: true,
            });
        },
        filteredConnections() {
            return this.connections.data.filter(it => it.isVerified);
        },
        filters() {
            return this.isLoggedIn && this.connections.data
                ? [
                      ...jobFilters(),
                      this.filteredConnections.length > 0
                          ? {
                                ...i18nMap('filterNames')('exclude_school_id'),
                                options: [
                                    ...this.connections.data
                                        .filter(it => it.isVerified)
                                        .map(it =>
                                            filterMap(
                                                it.school.subDomain
                                                    ? it.school.subDomain.toUpperCase()
                                                    : this.$t(
                                                          'cts.excludeSchoolIds.unknownSchoolId'
                                                      ),
                                                it.schoolId
                                            )
                                        ),
                                    i18nMap('excludeSchoolIds')('-1'),
                                ],
                            }
                          : null, // Can be null, needs to be filtered
                  ].filter(e => e) // Filters the null values
                : jobFilters();
        },
        hasFiltersChanged() {
            return false;
            if (this.forceFilterUpdate) {
                this.forceFilterUpdate = false;
                this.clearFilters();
            }

            if (!this.selectedFilters) return false;

            let changed = false;

            let empty = Object.entries(emptySelected(this.filters));

            empty.forEach(([key]) => {
                let temp = this.selectedFilters[key];
                if (temp && Object.entries(temp).length > 0) {
                    changed = true;
                }
            });

            return changed || Boolean(this.selectedFilters['company_names']);
        },
        shouldFakeLoad() {
            return this.$interfaces.fakeLoad.shouldFakeLoadFindJobs();
        },
        data() {
            return this.postings.data || [];
        },
    },
    methods: {
        loading() {
            return this.postings.loading || this.featured.loading;
        },
        error() {
            return this.postings.error;
        },
        clearFilters() {
            this.selected = emptySelected(this.filters);
        },
        getFilters() {
            return this.filters;
        },
        nextPage() {
            if (this.loading() || this.data.length < 4) {
                return;
            }

            this.$interfaces.scrollState.setFindJobsPage(this.page + 1);
        },
        resetPageScroll() {
            // Filters changed, need to reset page number and pagination anchors
            this.$interfaces.scrollState.setFindJobsPage(1);
            this.$refs.pagination.resetCurrentAnchor();
        },
        addQueryToFilters() {
            // Adds filters in query to selected
            if (!this.$route.query) {
                this.waitForFilterProcessing = false;
                return;
            }

            const selected = {};
            const query = Object.assign({}, this.$route.query);

            // Adding all filters in query to selected
            for (let filterName in query) {
                if (filterName === 'openLogin') continue;
                if (!selected[filterName]) {
                    selected[filterName] = {};
                }
                const filter = query[filterName];
                if (filter instanceof Array) {
                    filter.forEach(filterValue => {
                        selected[filterName][filterValue] = 1;
                    });
                } else if (filterName === 'query') {
                    selected[filterName] = query[filterName];
                } else {
                    selected[filterName][query[filterName]] = 1;
                }
            }

            // Set selected filters
            this.$nextTick(() => {
                this.selected = {
                    ...this.selected,
                    ...selected,
                };
                this.waitForFilterProcessing = false;
            });
        },
        addFiltersToQuery(filters) {
            const query = {}; // Empty query

            // Add all selected filters to query
            Object.entries(filters).forEach(([filterName, filterObject]) => {
                let filterValues = null;
                // Incase filter is a string (like search query)
                if (typeof filterObject === 'string') {
                    filterValues = filterObject;
                } else {
                    // filterObject is an object, get keys
                    filterValues = Object.keys(filterObject);
                }

                if (filterValues.length > 0) {
                    query[filterName] = filterValues;
                }
            });

            // Update url query
            this.$router.replace({ query });
        },
    },
    watch: {
        filters() {
            if (!this.selected) {
                this.selected = emptySelected(newVal);
            }
        },
        selected(filters) {
            if (filters) {
                this.addFiltersToQuery(filters);
            }
        },
        queries(newQueries, oldQueries) {
            if (oldQueries && Object.keys(oldQueries).length !== 0) {
                if (Object.keys(newQueries).length === 0) {
                    // Force refresh of filters
                    this.forceFilterUpdate = true;
                }
            }
        },
    },
    created() {
        this.selected = emptySelected(this.filters);
        this.$interfaces.floatingActionButton.show(() =>
            this.$scrollTo(this.$refs.top)
        );
    },
    beforeDestroy() {
        this.$interfaces.floatingActionButton.hide();
    },
    mounted() {
        if (this.$route.query.openLogin === 'true') {
            this.$interfaces.logInModule.toggleOpen();
        }
        this.addQueryToFilters();
    },
};
</script>

<style scoped>
.jobs-root {
    /* padding-bottom: 100px; */
}
</style>
